import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Search, Filter, Download, FileDown, Users, Target } from 'lucide-react';

/**
 * Componente de filtros da página de contatos - CORRIGIDO
 * 
 * CORREÇÕES APLICADAS:
 * - Removido SelectItems com value="" (erro do Radix Select)
 * - Corrigido comparação de tipos no filtro hasActiveFilters
 * - Mantida funcionalidade de filtros mas com valores válidos
 * 
 * FUNCIONALIDADES:
 * - Busca por nome, telefone ou email
 * - Filtros por tag, etapa, origem e serviço
 * - Sistema de exportação flexível
 * - Toggle para abrir/fechar área de filtros
 * - Contador de contatos total
 */

interface ContactsFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filters: {
    status: string;
    tag: string;
    origem: string;
    servico: string;
    apenasAnuncios: boolean;
    adName: string;
    adPlatform: string;
    hasActiveFilters: boolean;
  };
  onFilterChange: (key: string, value: string | boolean) => void;
  tags: Array<{ id: string; nome: string; cor?: string }>;
  etapas: Array<{ id: string; nome: string; cor?: string }>;
  totalContacts: number;
  isOpen: boolean;
  onToggle: () => void;
  // NOVAS PROPS para exportação
  onExport: (exportType?: 'all' | 'filtered' | 'selected') => Promise<void>;
  isExporting: boolean;
  hasActiveFilters: boolean;
  selectedCount: number;
  // NOVAS PROPS para anúncios
  availableAds: Array<{ value: string; label: string }>;
  availablePlatforms: Array<{ value: string; label: string }>;
}

export const ContactsFilters = ({
  searchQuery,
  onSearchChange,
  filters,
  onFilterChange,
  tags,
  etapas,
  totalContacts,
  isOpen,
  onToggle,
  onExport,
  isExporting,
  hasActiveFilters,
  selectedCount,
  availableAds = [],
  availablePlatforms = []
}: ContactsFiltersProps) => {
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Debug: Verificar aliases recebidos
  console.log('🎯 [ContactsFilters] Dados de anúncios recebidos:', {
    totalAds: availableAds?.length,
    primeiroAd: availableAds?.[0],
    exemploComAlias: availableAds?.find(ad => ad.label !== ad.value),
    todosAds: availableAds?.slice(0, 3) // Primeiros 3 para debug
  });

  // Função para lidar com exportação e fechar modal
  const handleExport = async (exportType: 'all' | 'filtered' | 'selected') => {
    await onExport(exportType);
    setExportModalOpen(false);
  };

  return (
    <div className="space-y-4">
      {/* Barra superior: Busca + Botões de ação */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Campo de busca */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Buscar por nome, telefone ou email..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 rounded-2xl border-green-200 focus:border-green-300 focus:ring-green-100"
          />
        </div>

        {/* Botões de ação */}
        <div className="flex gap-2">
          {/* Botão de Filtros */}
          <Button
            variant="outline"
            onClick={onToggle}
            className={`rounded-2xl border-green-200 hover:bg-green-50 hover:border-green-300 transition-all duration-200 ${isOpen ? 'bg-green-50 border-green-300' : ''
              }`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtros
            {hasActiveFilters && (
              <Badge className="ml-2 bg-green-100 text-green-800 hover:bg-green-100">
                {Object.entries(filters).filter(([key, value]) =>
                  key !== 'hasActiveFilters' &&
                  value &&
                  (typeof value === 'string' ? value.length > 0 : value === true)
                ).length}
              </Badge>
            )}
          </Button>

          {/* Botão de Exportação */}
          <Dialog open={exportModalOpen} onOpenChange={setExportModalOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                disabled={isExporting}
                className="rounded-2xl border-green-200 hover:bg-green-50 hover:border-green-300 transition-all duration-200"
              >
                <Download className={`h-4 w-4 mr-2 ${isExporting ? 'animate-spin' : ''}`} />
                Exportar
              </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileDown className="h-5 w-5 text-green-600" />
                  Exportar Contatos
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  Escolha quais contatos você deseja exportar:
                </p>

                {/* Opção: Todos os contatos */}
                <Button
                  variant="outline"
                  onClick={() => handleExport('all')}
                  disabled={isExporting}
                  className="w-full justify-start h-auto p-4 rounded-2xl border-green-200 hover:bg-green-50 hover:border-green-300"
                >
                  <div className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-green-600 mt-0.5" />
                    <div className="text-left">
                      <div className="font-medium">Todos os contatos</div>
                      <div className="text-sm text-gray-600">
                        Exportar todos os {totalContacts} contatos
                      </div>
                    </div>
                  </div>
                </Button>

                {/* Opção: Contatos filtrados */}
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    onClick={() => handleExport('filtered')}
                    disabled={isExporting}
                    className="w-full justify-start h-auto p-4 rounded-2xl border-green-200 hover:bg-green-50 hover:border-green-300"
                  >
                    <div className="flex items-start gap-3">
                      <Target className="h-5 w-5 text-green-600 mt-0.5" />
                      <div className="text-left">
                        <div className="font-medium">Contatos filtrados</div>
                        <div className="text-sm text-gray-600">
                          Exportar apenas contatos que atendem aos filtros atuais
                        </div>
                      </div>
                    </div>
                  </Button>
                )}

                {/* Opção: Contatos selecionados */}
                {selectedCount > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => handleExport('selected')}
                    disabled={isExporting}
                    className="w-full justify-start h-auto p-4 rounded-2xl border-green-200 hover:bg-green-50 hover:border-green-300"
                  >
                    <div className="flex items-start gap-3">
                      <Download className="h-5 w-5 text-green-600 mt-0.5" />
                      <div className="text-left">
                        <div className="font-medium">Contatos selecionados</div>
                        <div className="text-sm text-gray-600">
                          Exportar os {selectedCount} contatos selecionados
                        </div>
                      </div>
                    </div>
                  </Button>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Contador de contatos */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>{totalContacts} contatos encontrados</span>
      </div>

      {/* Área de filtros expandível */}
      {isOpen && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 space-y-4 shadow-sm animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center justify-between mb-2 md:hidden">
            <h3 className="font-medium text-green-900">Filtros Avançados</h3>
            <Button variant="ghost" size="sm" onClick={onToggle} className="h-8 w-8 p-0">
              <span className="sr-only">Fechar</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
            </Button>
          </div>
          <h3 className="font-medium text-green-900 mb-3 hidden md:block">Filtros Avançados</h3>

          {/* Primeira linha - Filtros existentes - Grid responsivo: 1 col mobile, 2 tablet, 4 desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Filtro por Etapa */}
            <div>
              <label className="block text-sm font-medium text-green-900 mb-2">
                Etapa
              </label>
              <Select
                value={filters.status}
                onValueChange={(value) => onFilterChange('status', value)}
              >
                <SelectTrigger className="rounded-xl border-green-200 focus:border-green-300 bg-white">
                  <SelectValue placeholder="Todas as etapas" />
                </SelectTrigger>
                <SelectContent>
                  {etapas.map((etapa) => (
                    <SelectItem key={etapa.id} value={etapa.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: etapa.cor || '#16A34A' }}
                        />
                        {etapa.nome}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filtro por Tag */}
            <div>
              <label className="block text-sm font-medium text-green-900 mb-2">
                Tag
              </label>
              <Select
                value={filters.tag}
                onValueChange={(value) => onFilterChange('tag', value)}
              >
                <SelectTrigger className="rounded-xl border-green-200 focus:border-green-300 bg-white">
                  <SelectValue placeholder="Todas as tags" />
                </SelectTrigger>
                <SelectContent>
                  {tags.map((tag) => (
                    <SelectItem key={tag.id} value={tag.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: tag.cor || '#3B82F6' }}
                        />
                        {tag.nome}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filtro por Origem */}
            <div>
              <label className="block text-sm font-medium text-green-900 mb-2">
                Origem
              </label>
              <Select
                value={filters.origem}
                onValueChange={(value) => onFilterChange('origem', value)}
              >
                <SelectTrigger className="rounded-xl border-green-200 focus:border-green-300 bg-white">
                  <SelectValue placeholder="Todas as origens" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="anuncio">Anúncio</SelectItem>
                  <SelectItem value="organico">Orgânico</SelectItem>
                  <SelectItem value="indicacao">Indicação</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="site">Site</SelectItem>
                  <SelectItem value="outros">Outros</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filtro por Serviço */}
            <div>
              <label className="block text-sm font-medium text-green-900 mb-2">
                Serviço
              </label>
              <Select
                value={filters.servico}
                onValueChange={(value) => onFilterChange('servico', value)}
              >
                <SelectTrigger className="rounded-xl border-green-200 focus:border-green-300 bg-white">
                  <SelectValue placeholder="Todos os serviços" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="avaliacao">Avaliação</SelectItem>
                  <SelectItem value="limpeza">Limpeza</SelectItem>
                  <SelectItem value="ortodontia">Ortodontia</SelectItem>
                  <SelectItem value="implante">Implante</SelectItem>
                  <SelectItem value="protese">Prótese</SelectItem>
                  <SelectItem value="emergencia">Emergência</SelectItem>
                  <SelectItem value="outros">Outros</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Segunda linha - Novos filtros de anúncios */}
          <div className="border-t border-green-200 pt-4">
            <h4 className="text-sm font-medium text-green-800 mb-3">Filtros de Anúncios</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              {/* Toggle "Apenas Anúncios" */}
              <div>
                <label className="block text-sm font-medium text-green-900 mb-2">
                  Filtrar Anúncios
                </label>
                <div className="flex items-center space-x-2 h-10 px-3 bg-white border border-green-200 rounded-xl hover:border-green-300 transition-colors">
                  <input
                    type="checkbox"
                    id="apenasAnuncios"
                    checked={filters.apenasAnuncios}
                    onChange={(e) => onFilterChange('apenasAnuncios', e.target.checked)}
                    className="rounded border-green-300 text-green-600 focus:ring-green-200 w-4 h-4 cursor-pointer"
                  />
                  <label
                    htmlFor="apenasAnuncios"
                    className="text-sm text-green-700 cursor-pointer select-none flex-1 font-medium"
                  >
                    Apenas anúncios
                  </label>
                </div>
              </div>

              {/* Filtro por Anúncio Específico */}
              <div>
                <label className="block text-sm font-medium text-green-900 mb-2">
                  Anúncio
                </label>
                <Select
                  value={filters.adName}
                  onValueChange={(value) => onFilterChange('adName', value)}
                  disabled={!filters.apenasAnuncios}
                >
                  <SelectTrigger className="rounded-xl border-green-200 focus:border-green-300 bg-white disabled:bg-gray-50 disabled:text-gray-400">
                    <SelectValue placeholder="Todos os anúncios" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableAds.map((ad) => (
                      <SelectItem key={ad.value} value={ad.value}>
                        {ad.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro por Plataforma */}
              <div>
                <label className="block text-sm font-medium text-green-900 mb-2">
                  Plataforma
                </label>
                <Select
                  value={filters.adPlatform}
                  onValueChange={(value) => onFilterChange('adPlatform', value)}
                  disabled={!filters.apenasAnuncios}
                >
                  <SelectTrigger className="rounded-xl border-green-200 focus:border-green-300 bg-white disabled:bg-gray-50 disabled:text-gray-400">
                    <SelectValue placeholder="Todas as plataformas" />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePlatforms.map((platform) => (
                      <SelectItem key={platform.value} value={platform.value}>
                        {platform.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2 md:hidden">
            <Button onClick={onToggle} className="w-full bg-green-600 text-white hover:bg-green-700">
              Ver Resultados
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
